import { assignInlineVars } from "@vanilla-extract/dynamic";
import React, { createContext } from "react";

import { getMergedTheme } from "chameleon/lib/components/design-system-provider/theme-merge";
import {
  Theme,
  ThemePartial,
  theme,
  themeClass,
  vars,
} from "chameleon/lib/theme/theme.css";

interface DesignSystemProviderProps {
  themeValueOverrides?: ThemePartial;
  children: React.ReactNode;
}

export const ThemeContext = createContext<Theme>(theme);

/**
 * Information for MPP team for integrating Design Settings with Theme Structure.
 * Sample themeValueOverrideStructure
 *
 * {
 *    typography: {
 *      fontSize: '14px',   // Body font size in pixel
 *      fontFamily: 'xyz"   // Typeface / font family
 *    }
 *    fontSizes: {
 *      h1: "32"            // Heading 1 font size in pixel [Don't put px at the end of th number]
 *      h2: "24"            // Heading 2 font size in pixel [Don't put px at the end of th number]
 *      h3: "18"            // Heading 3 font size in pixel [Don't put px at the end of th number]
 *      h4: "16"            // Heading 4 font size in pixel [Don't put px at the end of th number]
 *      body: "16"          // Body font size in pixel [Don't put px at the end of th number]
 *      caption: "14"       // caption font size in pixel [Don't put px at the end of th number]
 *      button: "16"        // Button font size in pixel [Don't put px at the end of th number]
 *    },
 *    fontWeights: {
 *      h1: "semibold"            // Heading 1 font weight
 *      h2: "medium"              // Heading 2 font weight
 *      h3: "medium"              // Heading 3 font weight
 *      h4: "medium"              // Heading 4 font weight
 *      body: "regular"           // Body font font weight
 *      caption: "regular"        // caption font weight
 *      button: "medium"          // button font weight
 *    },
 *    fontFamilies: {
 *      h1: "Inter"               // Heading 1 font family
 *      h2: "Inter"               // Heading 2 font family
 *      h3: "Inter"               // Heading 3 font family
 *      h4: "Inter"               // Heading 4 font family
 *      body: "Inter"             // Body font font family
 *      caption: "Inter"          // caption font family
 *      button: "Inter"           // button font family
 *    },
 *    color: {
 *      brand: "#0972c3"
 *      interactive: "#0972c3"
 *      pageBackground: "#fbfcfe"
 *      containerBackground: "#ffffff"
 *      formBackground: "#ffffff"
 *      primaryText: "#1e354d"
 *      secondaryText: "#808c9d"
 *      border: "#8595a8"
 *      divider: "#e4e8ec"
 *      critical: "#db271a"
 *      positive: "#008a09"
 *    },
 *    borderRadius: {
 *      form: "0",
 *      button: "0"
 *    }
 *    density: "default",
 * }
 */
export const DesignSystemProvider = ({
  children,
  themeValueOverrides = {},
}: DesignSystemProviderProps) => {
  // This should only override provided items but the type expects everything
  const themeMerged = getMergedTheme({ themeValueOverrides });

  const style = assignInlineVars(vars, themeMerged);

  return (
    <ThemeContext.Provider value={themeMerged}>
      <div
        className={`${themeClass.toString()} checkout-experience`}
        style={{ ...style }}
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
};
